<template>
  <div class="custom-gantt-wrapper">
    <!-- Header Controls -->
    <div class="gantt-controls">
      <h3 class="chart-title">Loyiha Gantt Grafiki</h3>
      <div class="zoom-controls">
        <button
          v-for="z in zoomLevels"
          :key="z.value"
          class="zoom-btn"
          :class="{ active: currentZoom === z.value }"
          @click="setZoom(z.value)"
        >
          {{ z.label }}
        </button>
      </div>
    </div>

    <!-- Main Container -->
    <div class="gantt-main">
      <!-- Sidebar / List -->
      <div class="gantt-sidebar">
        <div class="sidebar-header">Topshiriqlar ro'yxati</div>
        <div class="sidebar-content">
          <div 
            v-for="task in mappedTasks" 
            :key="task.id" 
            class="sidebar-row"
            @click="$emit('task-click', task._raw)"
          >
            <!-- Avatar -->
            <div class="avatar-box">
              <img v-if="task.avatar" :src="task.avatar" class="user-avatar" @error="onAvatarError"/>
              <div v-else class="user-avatar placeholder-avatar">
                <i class="fas fa-user"></i>
              </div>
            </div>
            
            <!-- Info -->
            <div class="task-info">
              <div class="task-name" :title="task.name">{{ task.name }}</div>
              <div class="task-meta">
                <span class="status-dot" :class="task.statusClass"></span>
                <span class="date-text">{{ formatDate(task.start) }} - {{ formatDate(task.end) }}</span>
              </div>
            </div>
          </div>
          <div v-if="!mappedTasks.length" class="empty-state m-3">Topshiriqlar mavjud emas.</div>
        </div>
      </div>

      <!-- Timeline Wrapper -->
      <div class="gantt-timeline-wrapper" ref="timelineWrapper">
        <div class="gantt-timeline" :style="{ width: totalGridWidth + 'px' }">
          
          <!-- Month Headers (Top Tier) -->
          <div class="timeline-header-top">
             <div 
               v-for="(m, i) in months" 
               :key="'m'+i" 
               class="month-cell"
               :style="{ width: (m.daysCount * dayWidth) + 'px' }"
             >
                {{ m.name }}
             </div>
          </div>
          
          <!-- Day Headers (Bottom Tier) -->
          <div class="timeline-header-bottom">
             <div 
               v-for="(day, idx) in days" 
               :key="'d'+idx" 
               class="day-cell" 
               :class="{ 'is-weekend': day.isWeekend, 'is-today': day.isToday }"
               :style="{ width: dayWidth + 'px' }"
             >
                <div class="day-num" v-if="dayWidth > 20">{{ day.dateNumber }}</div>
             </div>
          </div>

          <!-- Grid Background Lines -->
          <div class="timeline-grid">
            <div 
               v-for="(day, idx) in days" 
               :key="'grid'+idx" 
               class="grid-col"
               :class="{ 'is-weekend': day.isWeekend, 'is-today': day.isToday }"
               :style="{ left: (idx * dayWidth) + 'px', width: dayWidth + 'px' }"
            ></div>
          </div>

          <!-- Task Rows & Bars -->
          <div class="timeline-content">
             <div v-for="task in mappedTasks" :key="'row'+task.id" class="task-row">
               <!-- The Interactive Bar -->
               <div 
                 class="gantt-bar-wrapper"
                 :style="{
                    left: getBarLeft(task) + 'px',
                    width: getBarWidth(task) + 'px'
                 }"
                 @click="$emit('task-click', task._raw)"
               >
                 <div class="gantt-bar" :class="task.statusClass">
                   <span class="bar-label">{{ task.name }}</span>
                 </div>
                 
                 <!-- Resize Handles (Only for task owners) -->
                 <div 
                   v-if="canEdit(task)" 
                   class="resize-handle left-handle"
                   @mousedown.stop.prevent="startResize(task, 'start', $event)"
                 ></div>
                 <div 
                   v-if="canEdit(task)" 
                   class="resize-handle right-handle"
                   @mousedown.stop.prevent="startResize(task, 'end', $event)"
                 ></div>
               </div>
             </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Logic optimized for robust visualization and resize capabilities.
export default {
  name: 'CustomGantt',
  props: {
    tasks: { type: Array, default: () => [] },
    userId: { type: [String, Number], default: null },
    avatarResolver: { type: Function, default: null },
  },
  data() {
    return {
      zoomLevels: [
        { label: "Kun", value: "day" },
        { label: "Hafta", value: "week" },
        { label: "Oy", value: "month" },
      ],
      currentZoom: "week",
      dayWidths: { day: 80, week: 30, month: 10 },
      startDate: null,
      endDate: null,
      days: [],
      
      // Resizing state
      isResizing: false,
      resizeTask: null,
      resizeType: null,
      initialMouseX: 0,
      initialTimeMs: 0
    };
  },
  computed: {
    dayWidth() {
      return this.dayWidths[this.currentZoom] || 30;
    },
    mappedTasks() {
      if (!this.tasks.length) return [];
      const msDay = 86400000;
      
      return this.tasks.map(t => {
        const start = new Date(t.created_at || new Date()).getTime();
        let edStr = t.deadline;
        // fallback for deadline if absent
        const end = edStr ? new Date(edStr).getTime() : start + msDay; 

        let statusClass = 'bar-process';
        if ([3, 4].includes(t.column_id)) statusClass = 'bar-done';
        else if (t.column_id === 1) statusClass = 'bar-new';
        else if (end < Date.now() && ![3,4].includes(t.column_id)) statusClass = 'bar-danger';

        let avatar = '';
        if (this.avatarResolver && t.user_id) {
            avatar = this.avatarResolver(t.user_id);
        }

        return {
          id: t.id,
          name: t.text,
          start: start,
          end: Math.max(start + msDay, end), // Ensure min 1 day visually
          statusClass,
          userId: t.user_id,
          avatar,
          _raw: t
        };
      });
    },
    totalGridWidth() {
      return this.days.length * this.dayWidth;
    },
    months() {
      const monthsMap = [];
      let currentMonthStr = null;
      let monthDaysCount = 0;

      this.days.forEach((day) => {
        const d = new Date(day.time);
        const y = d.getFullYear();
        const mKey = `${day.shortMonth} ${y}`;
        if (!currentMonthStr) currentMonthStr = mKey;
        
        if (currentMonthStr === mKey) {
          monthDaysCount++;
        } else {
          monthsMap.push({ name: currentMonthStr, daysCount: monthDaysCount });
          currentMonthStr = mKey;
          monthDaysCount = 1;
        }
      });
      if (currentMonthStr) monthsMap.push({ name: currentMonthStr, daysCount: monthDaysCount });
      return monthsMap;
    }
  },
  watch: {
    tasks: {
      handler() { this.calculateDateRange(); },
      immediate: true,
      deep: true
    },
    currentZoom() {
      this.calculateDateRange();
    }
  },
  methods: {
    setZoom(val) {
      this.currentZoom = val;
    },
    calculateDateRange() {
      const msDay = 86400000;
      if (!this.mappedTasks.length) {
         const now = Date.now();
         this.startDate = new Date(now - (7 * msDay));
         this.endDate = new Date(now + (21 * msDay));
         this.buildDays();
         return;
      }
      
      let min = Math.min(...this.mappedTasks.map(t => t.start));
      let max = Math.max(...this.mappedTasks.map(t => t.end));
      
      // Pad graph with 1 week on left, 2 weeks on right for drag room
      this.startDate = new Date(min - (7 * msDay));
      this.endDate = new Date(max + (14 * msDay));
      this.buildDays();
    },
    buildDays() {
      const msDay = 86400000;
      const days = [];
      let current = new Date(this.startDate).setHours(0,0,0,0);
      const end = new Date(this.endDate).setHours(0,0,0,0);
      
      const months = ["Yan", "Fev", "Mar", "Apr", "May", "Iyun", "Iyul", "Avg", "Sen", "Okt", "Noy", "Dek"];
      const today = new Date().setHours(0,0,0,0);

      while (current <= end) {
        const d = new Date(current);
        days.push({
          time: current,
          dateNumber: d.getDate(),
          shortMonth: months[d.getMonth()],
          isWeekend: d.getDay() === 0 || d.getDay() === 6,
          isToday: current === today
        });
        current += msDay;
      }
      this.days = days;
    },
    getBarLeft(task) {
      if (!this.startDate) return 0;
      const msDay = 86400000;
      const diffMs = task.start - this.startDate.getTime();
      return (diffMs / msDay) * this.dayWidth;
    },
    getBarWidth(task) {
      const msDay = 86400000;
      let durMs = task.end - task.start;
      if (durMs < msDay) durMs = msDay; 
      return (durMs / msDay) * this.dayWidth;
    },
    formatDate(ms) {
      const d = new Date(ms);
      return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
    },
    canEdit(task) {
      // "faqat vazifa egasi" user wants ONLY task owner.
      return Number(task.userId) === Number(this.userId);
    },
    onAvatarError(e) {
      e.target.src = 'https://ui-avatars.com/api/?name=User&background=random';
    },

    // --- Resizing Logic ---
    startResize(task, type, event) {
      this.isResizing = true;
      this.resizeTask = task;
      this.resizeType = type;
      this.initialMouseX = event.clientX;
      this.initialTimeMs = type === 'start' ? task.start : task.end;

      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    },
    onMouseMove(event) {
      if (!this.isResizing) return;
      
      const msDay = 86400000;
      const deltaX = event.clientX - this.initialMouseX;
      const deltaDays = Math.round(deltaX / this.dayWidth);
      const deltaMs = deltaDays * msDay;
      
      if (this.resizeType === 'start') {
        const newStart = this.initialTimeMs + deltaMs;
        // Don't allow start to overlap end
        if (newStart < this.resizeTask.end) {
            this.resizeTask.start = newStart;
        }
      } else {
        const newEnd = this.initialTimeMs + deltaMs;
        // Don't allow end to be before start
        if (newEnd > this.resizeTask.start) {
            this.resizeTask.end = newEnd;
        }
      }
    },
    onMouseUp() {
      if (!this.isResizing) return;
      this.isResizing = false;
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
      
      // Propagate visual changes up to parent (which might save to API later)
      this.$emit('update-task-dates', {
        taskObj: this.resizeTask._raw,
        newStart: new Date(this.resizeTask.start),
        newEnd: new Date(this.resizeTask.end)
      });
    }
  }
}
</script>

<style scoped>
.custom-gantt-wrapper {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05); /* Premium depth */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 500px;
  font-family: 'Poppins', sans-serif;
  margin-top: 1rem;
}

/* Header */
.gantt-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
}
.chart-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}
.zoom-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 5px 12px;
  border-radius: 6px;
  margin-left: 6px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
}
.zoom-btn.active, .zoom-btn:hover {
  background: #3b82f6;
  color: #fff;
  border-color: #3b82f6;
}

/* Main Body Split */
.gantt-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Left Sidebar */
.gantt-sidebar {
  width: 250px;
  flex-shrink: 0;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  z-index: 10;
}
.sidebar-header {
  height: 45px;
  display: flex;
  align-items: center;
  padding: 0 15px;
  font-weight: 600;
  font-size: 12px;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
  background: #f1f5f9;
}
.sidebar-content {
  overflow-y: auto;
  flex: 1;
}
.sidebar-content::-webkit-scrollbar { width: 4px; }
.sidebar-content::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

.sidebar-row {
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s;
  box-sizing: border-box;
}
.sidebar-row:hover {
  background: #f1f5f9;
}
.avatar-box {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  margin-right: 10px;
}
.user-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.placeholder-avatar {
  background: #e2e8f0;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}
.task-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.task-name {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}
.task-meta {
  display: flex;
  align-items: center;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 4px;
}
.bar-new { background: #3b82f6 !important; }
.bar-process { background: #f59e0b !important; }
.bar-done { background: #10b981 !important; }
.bar-danger { background: #ef4444 !important; }

.date-text {
  font-size: 10px;
  color: #94a3b8;
}

/* Right Timeline */
.gantt-timeline-wrapper {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  background: #ffffff;
}
.gantt-timeline-wrapper::-webkit-scrollbar { height: 8px; }
.gantt-timeline-wrapper::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

.gantt-timeline {
  position: relative;
  height: 100%;
}

.timeline-header-top {
  display: flex;
  height: 25px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  box-sizing: border-box;
}
.month-cell {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #e2e8f0;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  box-sizing: border-box;
}

.timeline-header-bottom {
  display: flex;
  height: 25px;
  background: #fdfdfd;
  border-bottom: 1px solid #e2e8f0;
  box-sizing: border-box;
}

.day-cell {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #f1f5f9;
  font-size: 10px;
  color: #64748b;
  box-sizing: border-box;
}
.day-num {
  font-weight: 600;
  color: #1e293b;
}
.is-weekend {
  background-color: #fcfcfc;
}
.is-today {
  color: #3b82f6 !important;
  font-weight: 700;
}
.is-today .day-num { color: #3b82f6; }

/* Grid Lines Behind Bars */
.timeline-grid {
  position: absolute;
  top: 50px;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  pointer-events: none;
}
.grid-col {
  flex-shrink: 0;
  border-right: 1px dashed #f1f5f9;
  height: 100%;
  box-sizing: border-box;
}

/* Timeline Content (Bars) */
.timeline-content {
  position: absolute;
  top: 50px;
  left: 0;
  width: 100%;
}
.task-row {
  height: 48px;
  position: relative;
  border-bottom: 1px solid #f8fafc;
  box-sizing: border-box;
}

/* The Bar Itself */
.gantt-bar-wrapper {
  position: absolute;
  top: 10px;
  height: 28px;
  border-radius: 14px;
  cursor: pointer;
  z-index: 5;
}
.gantt-bar {
  width: 100%;
  height: 100%;
  border-radius: 14px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  box-sizing: border-box;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0,0,0,0.15);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: filter 0.2s, transform 0.2s;
}
.gantt-bar-wrapper:hover {
  z-index: 10;
}
.gantt-bar-wrapper:hover .gantt-bar {
  filter: brightness(1.05);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}
.bar-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Resize Handles */
.resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 10px;
  cursor: ew-resize;
  z-index: 15;
  background: rgba(255,255,255,0);
}
.left-handle {
  left: -2px;
}
.right-handle {
  right: -2px;
}
.resize-handle:hover {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 4px;
}
</style>
